// M8-ASSESS: sample answers for a small recruiting firm (the demo's Northwind Recruiting). One
// source for the engine tests, the web fake twin and `seed:demo`, so the report the tests pin is
// the report the demo shows. Hours are weekly; nothing here is a real person.
import type { Answers, TaskAnswer } from "../questionnaire/questionnaire";

const t = (
  name: string,
  hours: number,
  energy: number,
  enjoy: TaskAnswer["enjoy"],
  skill: string,
  pillar: string,
  someone_else: boolean,
  why: string,
): TaskAnswer => ({ name, hours_per_week: hours, energy, enjoy, skill, pillar, someone_else, why });

const OPS = "Operations & Delivery";
const REV = "Revenue & Sales";
const GROWTH = "Growth & Brand";
const STRAT = "Strategy & Culture";
const ADMIN = "Non Strategic";

export type SamplePersona = "owner" | "head" | "senior" | "am" | "sourcer" | "bookkeeper" | "coord";

export const SAMPLE_ANSWERS: Record<SamplePersona, Answers> = {
  owner: {
    role_title: "Managing director",
    clarity: {
      responsibilities: 4,
      success_definition: 4,
      pulled_into_others: 5,
      handoff_clarity: 2,
    },
    tasks: [
      t(
        "Client pitches and renewals",
        10,
        2,
        "love",
        "Advanced",
        REV,
        false,
        "Renewals are most of next year's revenue.",
      ),
      t(
        "Invoicing and chasing payments",
        6,
        -2,
        "dread",
        "Intermediate",
        ADMIN,
        true,
        "Keeps cash coming in so payroll never slips.",
      ),
      t(
        "Scheduling interviews for candidates",
        5,
        -1,
        "meh",
        "Intermediate",
        OPS,
        true,
        "Candidates drop out when interviews slip a week.",
      ),
      t(
        "Weekly pipeline review",
        3,
        1,
        "like",
        "Advanced",
        STRAT,
        false,
        "Shows which roles will close this month.",
      ),
      t(
        "Posting jobs on boards",
        4,
        -1,
        "meh",
        "Basic",
        GROWTH,
        true,
        "Without postings the pipeline dries up in two weeks.",
      ),
      t(
        "Hiring and coaching recruiters",
        6,
        2,
        "love",
        "Advanced",
        STRAT,
        false,
        "Our placements grow only as fast as the team does.",
      ),
      t(
        "Payroll and expenses",
        4,
        -2,
        "dread",
        "Basic",
        ADMIN,
        true,
        "People get paid on time and we stay compliant.",
      ),
      t(
        "Answering everyone's questions",
        8,
        -1,
        "meh",
        "Intermediate",
        OPS,
        false,
        "Unblocks the team when a process is unclear.",
      ),
    ],
    capacity_hours: 45,
    load_band: "overloaded",
    management: {
      mgmt_share: 40,
      team_load_read: "comfortable",
      team_clarity_read: 4,
      meeting_overhead_hours: 6,
      bottleneck: "Everything that touches a client goes through me.",
    },
    friction: {
      cracks: "Interview scheduling between recruiters and clients",
      invisible: "Fixing candidate paperwork late at night",
      drop: "Payroll and expenses",
    },
  },
  head: {
    role_title: "Head of recruiting",
    clarity: {
      responsibilities: 4,
      success_definition: 3,
      pulled_into_others: 3,
      handoff_clarity: 3,
    },
    tasks: [
      t(
        "Recruiter one-on-ones",
        4,
        1,
        "like",
        "Advanced",
        STRAT,
        false,
        "Recruiters who get coaching close more roles.",
      ),
      t(
        "Screening calls for senior roles",
        10,
        1,
        "like",
        "Advanced",
        OPS,
        false,
        "Senior placements carry the biggest fees.",
      ),
      t(
        "Interview scheduling with clients",
        6,
        -2,
        "dread",
        "Intermediate",
        OPS,
        true,
        "Slow scheduling loses candidates to other agencies.",
      ),
      t(
        "Updating the ATS after calls",
        5,
        -1,
        "meh",
        "Intermediate",
        OPS,
        true,
        "Clean records let anyone pick up a candidate.",
      ),
      t(
        "Weekly pipeline review",
        2,
        0,
        "like",
        "Advanced",
        STRAT,
        false,
        "We agree which roles need more sourcing.",
      ),
      t(
        "Client feedback calls",
        5,
        1,
        "like",
        "Intermediate",
        REV,
        false,
        "Feedback early keeps clients from going elsewhere.",
      ),
    ],
    capacity_hours: 40,
    load_band: "full",
    management: {
      mgmt_share: 25,
      team_load_read: "comfortable",
      team_clarity_read: 4,
      meeting_overhead_hours: 4,
      bottleneck: "Scheduling with clients eats the afternoons.",
    },
    friction: {
      cracks: "Interview scheduling between recruiters and clients",
      invisible: "Reworking job descriptions clients send",
      drop: "Updating the ATS after every call",
    },
  },
  senior: {
    role_title: "Senior recruiter",
    clarity: {
      responsibilities: 3,
      success_definition: 3,
      pulled_into_others: 4,
      handoff_clarity: 2,
    },
    tasks: [
      t(
        "Candidate screening calls",
        14,
        1,
        "like",
        "Advanced",
        OPS,
        false,
        "Screening is where we decide who goes forward.",
      ),
      t(
        "Interview scheduling",
        7,
        -2,
        "dread",
        "Intermediate",
        OPS,
        true,
        "Candidates need dates fast or they accept other offers.",
      ),
      t(
        "Sourcing on LinkedIn",
        8,
        0,
        "like",
        "Intermediate",
        GROWTH,
        false,
        "Fresh candidates for every open role this week.",
      ),
      t(
        "Reference checks",
        4,
        -1,
        "meh",
        "Intermediate",
        OPS,
        true,
        "Clients ask for two references before an offer.",
      ),
      t(
        "Offer negotiation",
        4,
        2,
        "love",
        "Advanced",
        REV,
        false,
        "Closing the offer is when we get paid.",
      ),
      t(
        "Covering for the coordinator",
        6,
        -1,
        "meh",
        "Basic",
        ADMIN,
        false,
        "Someone has to send the paperwork.",
      ),
    ],
    capacity_hours: 40,
    load_band: "drowning",
    management: null,
    friction: {
      cracks: "Interview scheduling between recruiters and clients",
      invisible: "Chasing candidates who stopped replying",
      drop: "Reference checks",
    },
  },
  am: {
    role_title: "Account manager",
    clarity: {
      responsibilities: 4,
      success_definition: 4,
      pulled_into_others: 2,
      handoff_clarity: 4,
    },
    tasks: [
      t(
        "Client check-ins",
        8,
        2,
        "love",
        "Advanced",
        REV,
        false,
        "Happy clients send the next role without a pitch.",
      ),
      t(
        "Writing proposals",
        5,
        1,
        "like",
        "Intermediate",
        REV,
        false,
        "Every new client starts from a proposal.",
      ),
      t(
        "CRM updates",
        4,
        -1,
        "meh",
        "Intermediate",
        REV,
        true,
        "Forecasts are only as good as the CRM.",
      ),
      t(
        "Invoicing clients",
        3,
        -1,
        "meh",
        "Intermediate",
        ADMIN,
        true,
        "Late invoices mean late cash.",
      ),
      t(
        "Quarterly business reviews",
        3,
        1,
        "like",
        "Advanced",
        STRAT,
        false,
        "Shows clients the value so they renew.",
      ),
    ],
    capacity_hours: 38,
    load_band: "comfortable",
    management: null,
    friction: {
      cracks: "Who sends the invoice after a placement",
      invisible: "Smoothing over missed interview slots with clients",
      drop: "CRM updates",
    },
  },
  sourcer: {
    role_title: "Sourcer",
    clarity: {
      responsibilities: 2,
      success_definition: 2,
      pulled_into_others: 4,
      handoff_clarity: 2,
    },
    tasks: [
      t(
        "Sourcing on LinkedIn",
        15,
        1,
        "like",
        "Intermediate",
        GROWTH,
        false,
        "Every search starts with a long list of candidates.",
      ),
      t(
        "Boolean searches on job boards",
        6,
        0,
        "like",
        "Intermediate",
        GROWTH,
        false,
        "Finds candidates who are not on LinkedIn.",
      ),
      t(
        "Posting jobs on boards",
        3,
        -1,
        "meh",
        "Basic",
        GROWTH,
        false,
        "Inbound applicants for open roles.",
      ),
      t(
        "Data entry into the ATS",
        4,
        -2,
        "dread",
        "Basic",
        OPS,
        true,
        "Recruiters need the candidate in the system.",
      ),
    ],
    capacity_hours: 40,
    load_band: "underloaded",
    management: null,
    friction: {
      cracks: "Interview scheduling between recruiters and clients",
      invisible: "Cleaning up duplicate candidates",
      drop: "Data entry into the ATS",
    },
  },
  bookkeeper: {
    role_title: "Part-time bookkeeper",
    clarity: {
      responsibilities: 4,
      success_definition: 3,
      pulled_into_others: 2,
      handoff_clarity: 3,
    },
    tasks: [
      t(
        "Reconciling the bank feed",
        4,
        0,
        "like",
        "Advanced",
        ADMIN,
        false,
        "The books match the bank every month.",
      ),
      t(
        "Month-end close",
        3,
        1,
        "like",
        "Advanced",
        ADMIN,
        false,
        "The owner gets numbers she can trust.",
      ),
      t(
        "Chasing receipts from the team",
        2,
        -2,
        "dread",
        "Intermediate",
        ADMIN,
        false,
        "Expenses can't be booked without them.",
      ),
    ],
    capacity_hours: 10,
    load_band: "comfortable",
    management: null,
    friction: {
      cracks: "Who sends the invoice after a placement",
      invisible: "",
      drop: "Chasing receipts",
    },
  },
  coord: {
    role_title: "Recruiting coordinator",
    clarity: null,
    tasks: [
      t("Interview scheduling", 12, -1, "meh", "Intermediate", OPS, false, ""),
      t("Candidate paperwork", 8, 0, "like", "Intermediate", OPS, false, ""),
    ],
    capacity_hours: 40,
    load_band: "full",
    management: null,
    friction: { cracks: "", invisible: "", drop: "" },
  },
};
